import * as React from "react";
import { Button } from "../ui/button";
import { SocialSvg } from "../SocialSvg";
import type { ReviewSourceSystem } from "@/services/bedrock";
import { cn } from "@/lib/utils";

const SOURCES: Array<{ id: ReviewSourceSystem; title: string }> = [
  { id: "GL", title: "Google" },
  { id: "FB", title: "Facebook" },
  { id: "ZL", title: "Zillow" },
  { id: "SC", title: "Internal" },
];

const ROW_ACTIVE_CLASS = "bg-slate-100 border-slate-400 text-slate-900";
const ROW_IDLE_CLASS = "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50";

type ReviewSourceFilterButtonsProps = {
  value: ReviewSourceSystem | null;
  onChange: (value: ReviewSourceSystem | null) => void;
  /** `icons` = circular icon buttons (popover). `expandable` = labeled row (table column). */
  variant?: "icons" | "expandable";
};

export function ReviewSourceFilterButtons({ value, onChange, variant = "icons" }: ReviewSourceFilterButtonsProps) {
  if (variant === "expandable") {
    return <ExpandableSourceFilterButtons value={value} onChange={onChange} />;
  }

  return (
    <div className="flex flex-row flex-wrap gap-1.5">
      {SOURCES.map(source => {
        const selected = value === source.id;
        return (
          <Button
            key={source.id}
            type="button"
            variant={selected ? "default" : "outline"}
            size="icon"
            className="rounded-full transition-all flex items-center justify-center"
            onClick={() => onChange(selected ? null : source.id)}
            title={source.title}
          >
            <SocialSvg
              networkId={source.id}
              size="xs"
              bgColorClass="bg-transparent"
              iconColorClass={selected && (source.id === "FB" || source.id === "ZL") ? "text-white" : undefined}
            />
          </Button>
        );
      })}
    </div>
  );
}

function ExpandableSourceFilterButtons({
  value,
  onChange,
}: {
  value: ReviewSourceSystem | null;
  onChange: (value: ReviewSourceSystem | null) => void;
}) {
  const [hoveredId, setHoveredId] = React.useState<ReviewSourceSystem | null>(null);
  const [focusedId, setFocusedId] = React.useState<ReviewSourceSystem | null>(null);
  const activeId = hoveredId ?? focusedId;

  return (
    <div
      className="flex h-14 w-full gap-1.5"
      role="group"
      aria-label="Platform"
      onMouseLeave={() => setHoveredId(null)}
    >
      {SOURCES.map(({ id, title }) => {
        const selected = value === id;
        const showLabel = activeId === id || (selected && activeId == null);
        const shortTitle = id === "SC" ? "Internal" : title;

        return (
          <button
            key={id}
            type="button"
            aria-pressed={selected}
            aria-label={title}
            title={title}
            className={cn(
              "flex h-full min-w-0 flex-col items-center justify-center overflow-hidden rounded-lg border px-1.5 transition-[flex-grow,flex-basis,colors] duration-300 ease-out cursor-pointer",
              showLabel ? "flex-[5] basis-0" : "flex-1 basis-0",
              selected ? ROW_ACTIVE_CLASS : ROW_IDLE_CLASS
            )}
            onMouseEnter={() => setHoveredId(id)}
            onFocus={() => setFocusedId(id)}
            onBlur={() => setFocusedId(current => (current === id ? null : current))}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onChange(selected ? null : id);
            }}
          >
            <span className="scale-125">
              <SocialSvg networkId={id} size="xs" bgColorClass="bg-transparent" />
            </span>
            <span
              className={cn(
                "grid w-full transition-[grid-template-rows,opacity] duration-300 ease-out",
                showLabel ? "grid-rows-[1fr] opacity-100 mt-1" : "grid-rows-[0fr] opacity-0 mt-0"
              )}
              aria-hidden={!showLabel}
            >
              <span className="min-h-0 overflow-hidden">
                <span className="block truncate text-[10px] font-bold uppercase tracking-wide leading-none pointer-events-none text-center">
                  {shortTitle}
                </span>
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
